import {
	DefaultButtonCompat as DefaultButton,
	PanelCompat as Panel,
	PrimaryButtonCompat as PrimaryButton,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import {SelectionMode, PanelType} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {EntityList, EntityListColumn} from 'components';
import {
	renderArrayField,
	renderRequirementStatus,
} from 'components/EntityList/ColumnRenderers';
import {RegulatoryDetailsPageFormMode} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocDetailsPage.types';
import {useRegulatoryDocumentsContext} from 'features/RegulatoryDocuments/context';
import {GetRegulatoryDocumentDetailsDocument} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {useRemoveRegulatoryDocumentParagraphsRequirementsMutation} from 'features/RegulatoryDocuments/hooks/useRemoveRegulatoryDocumentParagraphsRequirements.generated';
import {useRemoveGetexRegulationParagraphsRequirementsMutation} from 'features/RegulatoryDocuments/hooks/useRemoveGetexRegulationParagraphsRequirements.generated';
import {RegDocInjectedDataContext} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocInjectedDataContext';
import {RequirementsTooltipTranslationProvider} from 'features/Requirements/RequirementsTooltipTranslationProvider';
import {mapToRef} from 'helpers';
import {useCommand, useNotificationBar, useSelection} from 'hooks';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {Requirement, RequirementStatus, UserRole} from 'types';
import {useParagraphsContext} from './ParagraphsContext';
import {useCloneRequirement} from 'features/Requirements/hooks/useCloneRequirement';
import {useRequirementsVersions} from 'features/Requirements/hooks/useRequirementsVersions';
import {useGetRequirementsByIdsQuery} from 'features/RegulatoryDocuments/hooks/useGetAddRequirementsFormData.generated';
import {RequirementFromListPage} from 'features/Requirements/requirementPage.types';

export const RemoveRequirementsForm = () => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'RemoveRequirementsForm',
	});

	const isGetexInjected = React.useContext(RegDocInjectedDataContext) !== null;

	const [removeRegulatoryDocumentParagraphsRequirements] =
		useRemoveRegulatoryDocumentParagraphsRequirementsMutation();
	const [removeGetexRegulationParagraphsRequirements] =
		useRemoveGetexRegulationParagraphsRequirementsMutation();

	const {selectedRegulatoryDocument} = useRegulatoryDocumentsContext();
	const {selectedParagraphs} = useParagraphsContext();

	const paragraphRequirementIds = React.useMemo(
		() => selectedParagraphs.flatMap(p => p.requirements.map(r => r.id)),
		[selectedParagraphs],
	);

	const {data, loading} = useGetRequirementsByIdsQuery({
		variables: {ids: paragraphRequirementIds},
		skip: paragraphRequirementIds.length === 0,
	});
	const {cloneRequirement} = useCloneRequirement();
	const {isAdmin, isVexSupport} = useUserContext();
	const {setMessage} = useNotificationBar();
	const {hasEditableChildren} = useRequirementsVersions();

	const requirementData = React.useMemo(
		() =>
			data?.requirements.filter(
				req => !hasEditableChildren(req as RequirementFromListPage),
			) ?? [],
		[data],
	);

	const [mode, setMode] = React.useState<RegulatoryDetailsPageFormMode>(
		RegulatoryDetailsPageFormMode.None,
	);

	const [selectedRequirements, setSelectedRequirements] = React.useState<
		Requirement[]
	>([]);

	const [selection] = useSelection<Requirement>({
		onSelectionChanged(selectedItems) {
			setSelectedRequirements(selectedItems);
		},
		getKey: item => item.id,
	});

	const onRenderFooterContent: any = () => (
		<form onSubmit={e => e.preventDefault()}>
			<PrimaryButton styles={buttonStyles} onClick={handleSaveClick}>
				{t('RemoveButton')}
			</PrimaryButton>
			<DefaultButton onClick={handleCancelClick}>
				{t('CancelButton')}
			</DefaultButton>
		</form>
	);

	const selectedNonFinalRequirements = React.useMemo(
		() =>
			selectedRequirements?.filter(
				sr => sr.status !== RequirementStatus.Final,
			) || [],
		[selectedRequirements],
	);

	const handleRequirementVersioning = React.useCallback(async () => {
		const newRequirements = selectedRequirements.filter(
			req => req.status === RequirementStatus.Final,
		);
		const clonedRequirements = [];
		/* eslint-disable no-await-in-loop */
		for (const nr of newRequirements) {
			const result = await cloneRequirement(
				nr,
				[GetRegulatoryDocumentDetailsDocument],
				undefined,
				undefined,
			);
			clonedRequirements.push(
				result?.data?.createRequirement.requirement as Requirement,
			);
		}

		return clonedRequirements;
	}, [
		selectedRequirements,
		selectedParagraphs,
		cloneRequirement,
		GetRegulatoryDocumentDetailsDocument,
	]);

	const handleSaveClick = React.useCallback(async () => {
		const clonedRequirements = await handleRequirementVersioning();
		const requirementsToRemove = [
			...selectedNonFinalRequirements,
			...clonedRequirements,
		];
		if (selectedRegulatoryDocument?.id && requirementsToRemove.length > 0) {
			if (isGetexInjected) {
				await removeGetexRegulationParagraphsRequirements({
					variables: {
						input: {
							getexRegulationId: selectedRegulatoryDocument?.id ?? '',
							requirementRefs: mapToRef(requirementsToRemove),
							paragraphIds: selectedParagraphs.map(sp => sp.id) ?? [],
						},
					},
					refetchQueries: [GetRegulatoryDocumentDetailsDocument],
				});
			} else {
				await removeRegulatoryDocumentParagraphsRequirements({
					variables: {
						input: {
							regulatoryDocumentId: selectedRegulatoryDocument?.id ?? '',
							requirementRefs: mapToRef(requirementsToRemove),
							paragraphIds: selectedParagraphs.map(sp => sp.id) ?? [],
						},
					},
					refetchQueries: [GetRegulatoryDocumentDetailsDocument],
				});
			}
		}

		setMode(RegulatoryDetailsPageFormMode.None);
		setMessage(
			t('RemoveMessage', {
				selectedParagraphs: selectedParagraphs.length.toString(),
			}),
		);
		selection.setAllSelected(false);
	}, [
		mode,
		selectedParagraphs,
		selectedRegulatoryDocument,
		selectedRequirements,
	]);

	const handleCancelClick = React.useCallback(() => {
		setMode(RegulatoryDetailsPageFormMode.None);
	}, []);

	const [searchParams] = useSearchParams();

	const filter = searchParams.get('filter') ?? '';

	/* Requirment button, Regulatory Document */
	useCommand(
		{
			key: 'remove',
			text: t('Remove'),
			priority: 2,
			iconProps: {
				iconName: 'Remove',
			},
			disabled: selectedParagraphs.length === 0 || isVexSupport,
			hidden: filter === 'new' && !isAdmin,
			onClick: () => setMode(RegulatoryDetailsPageFormMode.Remove),
			roles: [UserRole.SystemAdministrator, UserRole.Vex],
			title: t('RemoveRequirementsTooltip'),
		},
		[mode, selectedParagraphs, isAdmin, filter],
	);

	const columns: EntityListColumn[] = React.useMemo(
		() => [
			{
				key: 'column1',
				name: t('Requirement'),
				fieldName: 'name',
				minWidth: 150,
				isResizable: true,
				sortable: true,
			},
			{
				key: 'column2',
				name: t('RequirementId'),
				fieldName: 'requirementId',
				minWidth: 150,
				isResizable: true,
				sortable: true,
			},
			{
				key: 'column3',
				name: 'Version',
				fieldName: 'version',
				minWidth: 150,
				isResizable: true,
				sortable: true,
			},
			{
				key: 'column4',
				name: t('Status'),
				fieldName: 'status',
				minWidth: 150,
				isResizable: true,
				onRender: renderRequirementStatus(),
				sortable: true,
			},
			{
				key: 'column5',
				name: t('VexCluster'),
				fieldName: 'vexClusters',
				minWidth: 150,
				isResizable: true,
				sortable: true,
				onRender: renderArrayField(),
			},
		],
		[t],
	);

	return (
		<Panel
			isLightDismiss
			type={PanelType.largeFixed}
			isFooterAtBottom={true}
			isOpen={mode === RegulatoryDetailsPageFormMode.Remove}
			onDismiss={handleCancelClick}
			closeButtonAriaLabel='Close'
			headerText={t('RemoveRequirementsPanelHeader')}
			onRenderFooterContent={onRenderFooterContent}
		>
			<Stack>
				<RequirementsTooltipTranslationProvider>
					<EntityList
						items={requirementData as any}
						columns={columns}
						aria-rowcount
						selectionMode={SelectionMode.multiple}
						selection={selection}
					/>
				</RequirementsTooltipTranslationProvider>
			</Stack>
		</Panel>
	);
};

const buttonStyles = {
	root: {
		marginRight: 8,
	},
};
